export type ReleaseChangeType = 'new' | 'improved' | 'fixed'

export type ReleaseModule =
  | 'requetes'
  | 'communications'
  | 'id'
  | 'permis'
  | 'loisirs'
  | 'plateforme'

export interface Localized {
  fr: string
  en: string
}

export interface ReleaseChange {
  type: ReleaseChangeType
  module: ReleaseModule
  /** Short title, shown in bold on the line and as the side panel title. */
  title: Localized
  /** One sentence on the benefit for the user. */
  text: Localized
}

export interface ReleaseNote {
  version: string
  /** ISO date */
  date: string
  title: Localized
  summary: Localized
  changes: ReleaseChange[]
}

export const releaseNotes: ReleaseNote[] = [
  {
    version: '4.8.0',
    date: '2026-10-13',
    title: { fr: 'Briefing territorial pour les élus', en: 'Territorial briefing for elected officials' },
    summary: {
      fr: 'Le tableau de bord des requêtes regroupe les enjeux du territoire et propose une prochaine étape pour chacun.',
      en: 'The requests dashboard groups territorial issues and suggests a next step for each.',
    },
    changes: [
      {
        type: 'new',
        module: 'requetes',
        title: { fr: 'Briefing territorial', en: 'Territorial briefing' },
        text: {
          fr: 'Comprenez en quelques secondes les enjeux de votre district ou de toute la ville, préparés par ATNA.',
          en: 'Understand your district’s or the whole city’s issues in seconds, prepared by ATNA.',
        },
      },
      {
        type: 'new',
        module: 'requetes',
        title: { fr: 'Brouillons préparés par ATNA', en: 'Drafts prepared by ATNA' },
        text: {
          fr: 'Demandez un suivi ou informez les citoyens en un clic, à partir d’un message déjà rédigé.',
          en: 'Request a follow-up or inform citizens in one click, from a message that’s already written.',
        },
      },
      {
        type: 'new',
        module: 'requetes',
        title: { fr: 'Période de 24 heures', en: '24-hour period' },
        text: {
          fr: 'Voyez d’un coup d’œil ce qui s’est passé depuis hier.',
          en: 'See at a glance what happened since yesterday.',
        },
      },
      {
        type: 'improved',
        module: 'requetes',
        title: { fr: 'Indicateurs simplifiés', en: 'Simpler indicators' },
        text: {
          fr: 'Les quatre chiffres clés tiennent sur une ligne et changent de période ensemble.',
          en: 'The four key figures fit on one row and change period together.',
        },
      },
    ],
  },
  {
    version: '4.7.0',
    date: '2026-09-30',
    title: { fr: 'Respect des SLA sur le territoire', en: 'SLA compliance across the territory' },
    summary: {
      fr: 'Carte par district et secteur, requêtes géolocalisées et assistant de données ATNA.',
      en: 'Map by district and sector, geolocated requests and ATNA data assistant.',
    },
    changes: [
      {
        type: 'new',
        module: 'requetes',
        title: { fr: 'Carte des districts', en: 'District map' },
        text: {
          fr: 'Repérez en un regard les districts et secteurs où les délais ne sont pas respectés.',
          en: 'Spot at a glance the districts and sectors where deadlines aren’t met.',
        },
      },
      {
        type: 'new',
        module: 'requetes',
        title: { fr: 'Requêtes sur la carte', en: 'Requests on the map' },
        text: {
          fr: 'Trouvez les requêtes en retard et comprenez pourquoi grâce au résumé ATNA.',
          en: 'Find late requests and understand why with the ATNA summary.',
        },
      },
      {
        type: 'new',
        module: 'requetes',
        title: { fr: 'Questions à ATNA', en: 'Ask ATNA' },
        text: {
          fr: 'Obtenez une réponse chiffrée en posant votre question en langage courant.',
          en: 'Get a figure-backed answer by asking your question in plain language.',
        },
      },
      {
        type: 'new',
        module: 'requetes',
        title: { fr: 'Rapport au conseil', en: 'Council report' },
        text: {
          fr: 'Préparez le rapport pour le conseil municipal sans recopier les chiffres.',
          en: 'Prepare the city council report without copying figures by hand.',
        },
      },
      {
        type: 'improved',
        module: 'plateforme',
        title: { fr: 'Données selon votre rôle', en: 'Data by role' },
        text: {
          fr: 'Chaque personne voit directement les données de son territoire ou de son service.',
          en: 'Everyone sees the data for their own territory or department right away.',
        },
      },
    ],
  },
  {
    version: '4.6.0',
    date: '2026-09-15',
    title: { fr: 'Gestion des requêtes', en: 'Request management' },
    summary: {
      fr: 'Recherche, nouveaux statuts et collaboration interne simplifiée.',
      en: 'Search, new statuses and simpler internal collaboration.',
    },
    changes: [
      {
        type: 'new',
        module: 'requetes',
        title: { fr: 'Recherche par adresse', en: 'Search by address' },
        text: {
          fr: 'Retrouvez une requête en tapant une rue ou le nom du demandeur.',
          en: 'Find a request by typing a street or the requester’s name.',
        },
      },
      {
        type: 'improved',
        module: 'requetes',
        title: { fr: 'Statuts plus lisibles', en: 'Clearer statuses' },
        text: {
          fr: 'Distinguez d’un regard les demandes nouvelles, en cours et complétées.',
          en: 'Tell new, in-progress and completed requests apart at a glance.',
        },
      },
      {
        type: 'improved',
        module: 'requetes',
        title: { fr: 'Note interne', en: 'Internal note' },
        text: {
          fr: 'Partagez le contexte d’une requête avec l’équipe, sans que le citoyen le voie.',
          en: 'Share a request’s context with the team, without the citizen seeing it.',
        },
      },
      {
        type: 'fixed',
        module: 'requetes',
        title: { fr: 'Requêtes en anglais', en: 'Requests in English' },
        text: {
          fr: 'Le module s’affiche maintenant dans la langue choisie en haut de page.',
          en: 'The module now displays in the language chosen at the top of the page.',
        },
      },
    ],
  },
  {
    version: '4.5.0',
    date: '2026-08-28',
    title: { fr: 'Audience et canaux des avis', en: 'Notice audience and channels' },
    summary: {
      fr: 'Ciblage « Où × Qui », nouveaux canaux Application et Écrans publics.',
      en: '“Where × Who” targeting, new App and Public screens channels.',
    },
    changes: [
      {
        type: 'new',
        module: 'communications',
        title: { fr: 'Canal Application', en: 'App channel' },
        text: {
          fr: 'Joignez les citoyens directement dans le fil de l’app, avec un aperçu court et le détail.',
          en: 'Reach citizens right in the app feed, with a short preview and the full detail.',
        },
      },
      {
        type: 'new',
        module: 'communications',
        title: { fr: 'Écrans publics', en: 'Public screens' },
        text: {
          fr: 'Diffusez vos avis sur les écrans des bâtiments municipaux.',
          en: 'Show your notices on the screens in municipal buildings.',
        },
      },
      {
        type: 'improved',
        module: 'communications',
        title: { fr: 'Ciblage de l’audience', en: 'Audience targeting' },
        text: {
          fr: 'Ciblez précisément un secteur, quelques adresses ou une tranche d’âge, et combinez-les.',
          en: 'Target a sector, a few addresses or an age bracket precisely, and combine them.',
        },
      },
      {
        type: 'new',
        module: 'communications',
        title: { fr: 'Équipes internes', en: 'Internal teams' },
        text: {
          fr: 'Informez une équipe de la Ville en la choisissant comme audience.',
          en: 'Notify a City team by choosing it as the audience.',
        },
      },
      {
        type: 'new',
        module: 'communications',
        title: { fr: 'Traduction automatique', en: 'Automatic translation' },
        text: {
          fr: 'Obtenez la version anglaise ou française de votre avis en un clic.',
          en: 'Get the English or French version of your notice in one click.',
        },
      },
    ],
  },
  {
    version: '4.4.0',
    date: '2026-08-10',
    title: { fr: 'Modèles et catégories', en: 'Templates and categories' },
    summary: {
      fr: 'Création, modification et suppression des modèles et des catégories d’avis.',
      en: 'Create, edit and delete notice templates and categories.',
    },
    changes: [
      {
        type: 'new',
        module: 'communications',
        title: { fr: 'Modèles d’avis', en: 'Notice templates' },
        text: {
          fr: 'Réutilisez un avis existant au lieu de repartir de zéro.',
          en: 'Reuse an existing notice instead of starting from scratch.',
        },
      },
      {
        type: 'new',
        module: 'communications',
        title: { fr: 'Gestion des catégories', en: 'Category management' },
        text: {
          fr: 'Organisez vos avis avec vos propres catégories, sans risque de suppression par erreur.',
          en: 'Organise your notices with your own categories, with no risk of deleting by mistake.',
        },
      },
      {
        type: 'improved',
        module: 'communications',
        title: { fr: 'Liste des avis compacte', en: 'Compact notice list' },
        text: {
          fr: 'Voyez plus d’avis à l’écran et repérez leurs canaux d’un coup d’œil.',
          en: 'See more notices on screen and spot their channels at a glance.',
        },
      },
      {
        type: 'improved',
        module: 'id',
        title: { fr: 'Filtres harmonisés', en: 'Consistent filters' },
        text: {
          fr: 'Les filtres du module ID fonctionnent comme ceux de Communications.',
          en: 'The ID module filters work the same way as in Communications.',
        },
      },
    ],
  },
]
